import EmailTag from './EmailTag';
import type { TagVariant } from './EmailTag';
import { editProposalTag } from '../../utils/editProposalTag';
import type { PortalEmailEditProposal } from '../../types/portal';
import { getOrderStatusDisplay } from '../../utils/orderStatusLabels';
import type { OrderStatusVariant } from '../../utils/orderStatusLabels';
import styles from './EmailListItem.module.css';
import { formatLateSyncLabel, lateSyncTitle } from '../../utils/lateSync';

export interface EmailData {
  id: string;
  from: string;
  email: string;
  subject: string;
  preview: string;
  time: string;
  /** ISO timestamp for client-side formatting (browser timezone). */
  receivedAt?: string;
  isUnread: boolean;
  isFlagged: boolean;
  isNewCustomer: boolean;
  tags: string[];
  isAssigned: boolean;
  assignedTo?: string;
  status: string;
  hasOrder?: boolean;
  orderRecordStatus?: string;
  hasStateRouting?: boolean;
  shipping?: {
    state: string;
  };
  priorityScore?: number;
  needsAttention?: boolean;
  attentionReasons?: string[];
  threadId?: string;
  emailCount?: number;
  daysStale?: number;
  // Late-synced email: reached the store days after it was sent (catch-up
  // sync). Rendered with receivedAt as "Received Jul 30 · synced Sep 25" so
  // it never reads as a fresh customer email.
  syncedAt?: string;
  lateSyncDays?: number;
  isInternal?: boolean;
  // Rep firm that routed this email (from matched_salesperson)
  routedVia?: string;
  // Deterministic extraction-confidence bucket. NULL on rows from
  // before the bucket rollout — those skip the pill.
  confidenceBucket?: 'high' | 'medium' | 'low' | null;
  // Non-blocking duplicate-PO warning (e.g. "PO 12345 matches order SO-001").
  // Set when this email's draft shares a (customer, PO) with an existing order.
  // The draft is created anyway; this badge just alerts the CSR.
  dupPoWarning?: string;
  // AI-drafting progress: 'drafting' while the email is being classified
  // (don't create a manual order — a draft appears shortly), 'failed' when
  // the AI gave up or is stuck past the 10-min timeout (handle manually).
  aiDraftState?: 'drafting' | 'failed' | null;
  // Merlin edit proposal this email/thread produced — a customer change
  // request shown on the email like a linked order (badge here, card in the
  // opened email). Null on the common no-revision path.
  editProposal?: PortalEmailEditProposal | null;
}

/** Map order status variant to EmailTag variant. */
const ORDER_TAG_VARIANT: Record<OrderStatusVariant, TagVariant> = {
  detected: 'order-detected',
  draft: 'order-draft',
  submitted: 'order-submitted',
  confirmed: 'order-confirmed',
  shipped: 'order-shipped',
  completed: 'order-completed',
  cancelled: 'order-cancelled',
  failed: 'order-failed',
};

interface EmailListItemProps {
  email: EmailData;
  isSelected: boolean;
  isChecked?: boolean;
  onClick: () => void;
  onCheckboxChange?: (checked: boolean) => void;
}

const EmailListItem: React.FC<EmailListItemProps> = ({
  email,
  isSelected,
  isChecked,
  onClick,
  onCheckboxChange,
}) => {
  return (
    <div
      className={`${styles.item} ${isSelected ? styles.selected : ''} ${email.isUnread ? styles.unread : styles.read} ${email.needsAttention ? styles.attention : ''}`}
      onClick={onClick}
      data-action="email_open"
      data-outcome="open"
      data-target-email-id={email.id}
    >
      <input
        type="checkbox"
        className={styles.checkbox}
        data-action="email_select"
        checked={isChecked ?? false}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onCheckboxChange?.(e.target.checked)}
      />
      <div className={styles.content}>
        <div className={styles.header}>
          <div className={styles.from}>
            {email.from}
            {email.isFlagged && (
              <svg className={styles.flagIcon} viewBox="0 0 24 24" fill="#EE3C33">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            )}
            {email.needsAttention && (
              <span className={styles.attentionDot} title={email.attentionReasons?.join(', ')} />
            )}
          </div>
          <div className={styles.headerRight}>
            {(email.emailCount ?? 1) > 1 && (
              <span className={styles.chainBadge} title={`${email.emailCount} messages in this chain`}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="2" y="4" width="20" height="5" rx="1" />
                  <rect x="2" y="12" width="20" height="5" rx="1" />
                </svg>
                {email.emailCount}
              </span>
            )}
            {email.daysStale != null && email.daysStale >= 5 && (
              <span className={`${styles.staleBadge} ${email.daysStale >= 7 ? styles.staleCritical : ''}`}>
                {email.daysStale}d
              </span>
            )}
            {email.lateSyncDays != null && (
              <span
                className={styles.lateSyncBadge}
                data-testid="late-sync-badge"
                title={lateSyncTitle(email.lateSyncDays, email.receivedAt, email.syncedAt)}
              >
                {formatLateSyncLabel(email.receivedAt, email.syncedAt)}
              </span>
            )}
            {email.priorityScore != null && (
              <span className={styles.priorityBadge}>
                Priority: {Math.round(email.priorityScore * 100)}
              </span>
            )}
            <span className={styles.time}>{email.time}</span>
          </div>
        </div>
        <div className={styles.subject}>{email.subject}</div>
        <div className={styles.preview}>{email.preview}</div>
        <div className={styles.tags}>
          {email.aiDraftState === 'drafting' && (
            <EmailTag
              variant="ai-drafting"
              label="AI is drafting…"
              title="The AI is processing this email — if it's an order, a draft will appear shortly. No need to create one manually."
            />
          )}
          {email.aiDraftState === 'failed' && (
            <EmailTag
              variant="ai-failed"
              label="AI failed — handle manually"
              title="The AI could not finish processing this email (failed or timed out after 10 minutes). Please review it and create any order manually."
            />
          )}
          {email.isInternal && <EmailTag variant="internal" label="Internal" />}
          {email.isNewCustomer && <EmailTag variant="new-customer" label="New Customer" />}
          {(email.hasOrder || email.orderRecordStatus) && (() => {
            const display = getOrderStatusDisplay(email.orderRecordStatus);
            return <EmailTag variant={ORDER_TAG_VARIANT[display.variant]} label={display.tagLabel} />;
          })()}
          {!email.hasOrder && !email.orderRecordStatus && email.tags.includes('order') && <EmailTag variant="order" label="Order Request" />}
          {(() => {
            const tag = editProposalTag(email.editProposal);
            return tag ? <EmailTag variant={tag.variant} label={tag.label} title={tag.title} /> : null;
          })()}
          {email.dupPoWarning && (
            <EmailTag variant="dup-po" label="Duplicate PO" title={email.dupPoWarning} />
          )}
          {email.confidenceBucket && (
            <EmailTag
              variant={`confidence-${email.confidenceBucket}` as TagVariant}
              label={`AI confidence: ${email.confidenceBucket}`}
            />
          )}
          {email.tags.includes('urgent') && <EmailTag variant="urgent" label="Urgent" />}
          {email.needsAttention && email.attentionReasons?.map((reason) => (
            <EmailTag key={reason} variant="urgent" label={reason} />
          ))}
          {email.routedVia && (
            <span className={styles.routedVia}>via {email.routedVia}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailListItem;
